import * as R from "ramda";
import { CourseChanges, IClientPatch, IPatchAction } from "../../components/course-author/helpers/patches";
import {
  CourseContent,
  CourseContentType,
  CoursePlayer,
  DefaultWide,
  FullResponsiveWide,
  ICourseContent,
  ICourseLocaleSettings,
  ICourseLocalizedText,
  ICourseMetadata,
  ICoursePublication,
  ICourseSet,
  IFullCourse,
  IMultiLocaleCourse,
  QuestionDrawingPreference,
  SaltWide,
} from "../../components/course-author/types";
import { PreferredLocale } from "../../utils/locales";

const CoursePlayerToWidth = {
  [DefaultWide]: "24em",
  [SaltWide]: "36em",
  [FullResponsiveWide]: "36em",
};

const PlayerImport = '@import "./player.less";';

export const cloneCourse = (
  course: IFullCourse,
  accountId: string,
  organizationId: string,
  suffix: string
): IFullCourse =>
  R.mergeDeepRight<IFullCourse, IFullCourse>(course, {
    state: {
      admins: [`acc.${accountId}`],
      contentType: CourseContentType.HtmlContent,
      deleted: false,
      listedIn: [organizationId],
      meta: {
        mLTitle: {
          elems: course.state.meta.mLTitle.elems.map(cloneTitle(suffix)),
        },
        title: `${course.state.meta.title}${suffix}`,
      },
      owner: organizationId,
      publications: { nextId: 0, elems: [] } as ICourseSet<ICoursePublication>,
      style: null,
    },
  } as IFullCourse) as IFullCourse;

export const cloneTitle = (suffix: string) => (text: ICourseLocalizedText): ICourseLocalizedText => ({
  ...text,
  value: {
    ...text.value,
    text: `${text.value.text}${suffix}`,
  },
});

export const uniqCreatorIds = (changes: CourseChanges): string[] =>
  R.compose<Array<IClientPatch<IPatchAction>>, string[], string[]>(
    R.uniq,
    R.map<IClientPatch<IPatchAction>, string>(change => `acc.${change.createdBy}?display`)
  )(changes);

export const generateChangeIds = (lastVersionId: number, courseId: string, changesCount: number) =>
  new Array(lastVersionId)
    .fill(lastVersionId)
    .map((_, step) => `crs.${courseId}?c${lastVersionId - step}`)
    .slice(0, changesCount);

export function zipFilterResponseData<T>(
  keys: string[],
  response: { data: Array<{ value: T }> }
): Array<R.KeyValuePair<string, T>> {
  return R.filter(([_, res]) => !R.isNil(res), R.zip(keys, response.data.map(R.prop<string, T>("value"))));
}

const getResolutionChange = (coursePlayer: CoursePlayer) => {
  if (typeof coursePlayer === "string") {
    const resolution = CoursePlayerToWidth[coursePlayer as string];
    return `@standard-container-width: ${resolution} * @column-width;
@site-content-padding: (${resolution}/24);`;
  } else {
    return `@standard-container-width: ${coursePlayer.percentage}%;`;
  }
};

export const createStyleForCourse = (coursePlayer: CoursePlayer, background: string | null) => {
  const style: string[] = [];
  const backgroundStyle = `@media (min-width: 900px) {
  div#jsonp-content {
    background-image: url("${background}") !important;
  }
}`;
  const containerWidth = getResolutionChange(coursePlayer);
  style.push(PlayerImport, containerWidth);

  if (background) style.push(backgroundStyle);

  return style.join("\n");
};

export const createImageUrl = (root: string, imgId: string) => `${root}/images/${imgId}/_download`;

export const createNewCourse = (accountId: string, owner: string): IMultiLocaleCourse => {
  const admins = [`acc.${accountId}`];
  const listedIn = [owner];
  const deleted = false;
  const background = null;
  const clonedFrom = null;
  const coursePlayer = SaltWide;
  const locked = false;
  const readSpeaker = true;
  const emptySet: ICourseSet<any> = { elems: [], nextId: 0 };
  const publications: ICourseSet<ICoursePublication> = emptySet;
  const style = null;
  const meta: ICourseMetadata = {
    description: "",
    icon: "",
    mLDescription: emptySet,
    mLTitle: emptySet,
    tags: [],
    title: "New Course",
  };
  const localeSettings: ICourseLocaleSettings = {
    masterLocale: PreferredLocale.en,
    supportedLocales: [PreferredLocale.en],
  };
  const content: ICourseContent = {
    type: CourseContent.MultiLocale,
    value: {
      root: {
        children: emptySet,
        settings: {
          doPostAssessment: true,
          doPreAssessment: true,
          showCorrectAnswersAfterAssessment: true,
        },
        test: {
          accumulateAnswers: true,
          isRandomized: true,
          limitAllowedMistakes: true,
          numAllowedMistakes: 0,
          numCorrectAnswersRequired: 1,
          questionBank: emptySet,
          questionDrawingPreference: QuestionDrawingPreference.PreferNewOrWrongQuestions,
        },
        title: emptySet,
      },
      settings: {
        customCompletedMessage: emptySet,
        linear: false,
        useCustomCompletedMessage: false,
      },
    },
    version: 0,
  };
  const contentType = CourseContentType.HtmlContent;
  return {
    admins,
    background,
    clonedFrom,
    content,
    contentType,
    coursePlayer,
    deleted,
    listedIn,
    localeSettings,
    locked,
    meta,
    owner,
    publications,
    readSpeaker,
    style,
  };
};

export const getPositionPart = (elementPosition: string) => {
  const positionParts = elementPosition.split(".");
  const lastPositionPart = positionParts[positionParts.length - 1];
  if (lastPositionPart === "test") {
    return "questionBank";
  }
  return "parts";
};
